import qs from 'query-string';

import clearSessionStorageJwtKey from 'actions/clearSessionStorageJwtKey';
import removeJwt from 'actions/removeJwt';
import { JWT } from 'constants/system';
import client, { createClient } from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';
import { getCookie } from 'utils/cookies';

const paramsSerializer = params =>
  qs.stringify(params, {
    arrayFormat: 'comma',
    encode: false,
  });

/**
 * This endpoint is used by useComment hook to create new comments.
 * If current user is a K12Admin/Guardian, student must be passed into params.
 * If current user is a Student, student should not be passed in.
 * @param {number|string} id - The ILP ID
 * @param {number|string} student - The student id
 * @param {string} content - The comment
 */
export const createComment = ({ version = 'v1', id, student, content } = {}) =>
  client
    .request({
      url: `${version}/ilp/student-ilps/${id}/comments/`,
      method: RequestMethod.POST,
      params: {
        student,
      },
      data: {
        content,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * This endpoint is used by useComment hook to create new comments.
 * This is for users that are not logged in (guardians)
 * @param {number|string} id - The ILP ID
 * @param {number|string} nonLoggedInUserName - The user's name
 * @param {string} content - The comment
 */
export const createCommentForGuardian = ({
  version = 'v1',
  id,
  nonLoggedInUserName,
  content,
} = {}) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/${id}/create-comment/`,
      method: RequestMethod.POST,
      data: {
        nonLoggedInUserName,
        content,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * This endpoint is used by useComment hook to fetch all comments for a plan
 * If current user is a K12Admin/Guardian, student must be passed into params.
 * If current user is a Student, student should not be passed in.
 * @param {number|string} id - The ILP ID
 * @param {number|string} student - The student id
 */
export const getComments = ({ version = 'v1', id, student } = {}) =>
  client
    .request({
      url: `${version}/ilp/student-ilps/${id}/comments/`,
      method: RequestMethod.GET,
      params: {
        student,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * This endpoint is used by useComment hook to fetch all comments for a plan
 * This is for users that are not logged in (guardians)
 * @param {number|string} id - The ILP ID
 */
export const getCommentsForGuardian = ({ version = 'v1', id } = {}) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/${id}/list-comments/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * This endpoint is used by useComment hook to update existing comments.
 * If current user is a K12Admin/Guardian, student must be passed into params.
 * If current user is a Student, student should not be passed in.
 * @param {number|string} id - The ILP ID
 * @param {number|string} student - The student id
 * @param {string} hashId - The hash id of the comment that requires updating
 * @param {string} content - The comment
 */
export const updateComment = ({
  version = 'v1',
  id,
  student,
  hashId,
  content,
} = {}) =>
  client
    .request({
      url: `${version}/ilp/student-ilps/${id}/comments/${hashId}/`,
      method: RequestMethod.PATCH,
      params: {
        student,
      },
      data: {
        content,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Allows districts to update the dates where an ILP is editable
 *
 * @param {string} uuid - uuid of the ILP
 * @param {Object} updatedDate - updated start or end date
 */
export const updateEditablePlanDates = ({
  version = 'v1',
  uuid,
  updatedDate,
} = {}) =>
  client
    .request({
      url: `${version}/ilp/district-ilps/${uuid}/`,
      method: RequestMethod.PATCH,
      data: updatedDate,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns list of students related to ILP
 *
 * @param params - filters applied to list
 */
export const getStaffILPs = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ilp/staff-ilps/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns list of student hashes related to ILP - used for bulk messaging and PDF download
 *
 * @param params - filters applied to list
 */
export const getStudentsHash = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ilp/staff-ilps/hash/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns previous and next ILPS
 *
 * @param params - filters applied to list
 */
export const getStaffILPsNavigation = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ilp/staff-ilps/navigation/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns student PGP reports in PDF format
 *
 * @param params - filters applied to list
 */
export const downloadPGPReport = ({ version = 'v1', payload } = {}) =>
  client
    .request({
      url: `${version}/ilp/pgp-report/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns list of student hashes related to ILP - used for bulk messaging, PDF
 * download and fetching plan element details
 * @param params - filters applied to list
 */
export const getILPContext = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ilp/staff-ilps/context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns list of ILP deadlines including scope, school, grades, and date
 */
export const getILPDeadlines = ({ version = 'v2', params } = {}) =>
  client
    .request({
      url: `${version}/indicators/indicator-deadlines/personalized-plan/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Downloads a plan's experience tracking session
 */
export const downloadExperienceTrackingCSV = ({
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/csv/`,
      method: RequestMethod.POST,
      paramsSerializer: { serialize: paramsSerializer },
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by authenticated guardians to retrieve the ILP uuid and student id
 * @param ilpId - The id of the ILP plan. This is provided when visiting
 * GuardianILPLandingPage
 */
export const getILPMetadata = ({ version = 'v1', ilpId } = {}) =>
  client
    .request({
      url: `${version}/ilp/guardian-retrieve-ilp/${ilpId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentContactInfo = ({ version = 'v1', studentIlpId } = {}) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/${studentIlpId}/get-student-contact-info/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateUserContactInfo = ({
  version = 'v1',
  studentIlpId,
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/${studentIlpId}/update-student-contact-info/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const generateResume = ({ version = 'v1', studentIlpId } = {}) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/${studentIlpId}/student-resume/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createAddress = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/create-address/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateAddress = ({ addressId, payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/update-address/${addressId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Fetches all ilp element uuids associated with a plan. Each uuid is then
 * used by getElementDetails to fetch the element's details.
 * @param districtUuid {string} - Required if calling as K12Admin OR student
 * @param studentId {number} - Required if calling as K12Admin
 */
export const getPlanDetails = ({ version = 'v1', districtUuid, studentId }) =>
  client
    .request({
      url: studentId
        ? `${version}/ilp/plan/${districtUuid}/${studentId}`
        : `${version}/ilp/plan/${districtUuid}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Fetches the ilp element's details.
 * @param ilpElementUuid {string} - The plan element's uuid
 * @param studentIlpUuid {string} - The plan's ilp uuid
 */
export const getElementDetails = ({
  version = 'v1',
  ilpElementUuid,
  studentIlpUuid,
}) =>
  client
    .request({
      url: `${version}/ilp/ilp-element-details/${ilpElementUuid}/${studentIlpUuid}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Updates the ilp element's data
 * @param type {string} - The ilp element type
 * @param id {string} -  The uuid of the current ilp element
 * @param studentId {string} - If k12admin, this is required
 * @param payload.districtIlp {string} - The selected district ilp uuid
 */
export const submitAnswer = ({
  version = 'v1',
  type,
  id,
  studentId = undefined,
  payload,
}) =>
  client
    .request({
      url: studentId
        ? `${version}/ilp/${type}/element/${id}/${studentId}/`
        : `${version}/ilp/${type}/element/${id}/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Approves a student's plan
 * @param planId {string} - The uuid of the current ilp element
 * @param studentId {string} - The id of the student
 * @param payload.guardianApprovedDate - If guardian, add current datetime
 * @param payload.schoolAdminApprovedDate - else add current datetime
 */
export const approveStudentPlan = ({
  version = 'v1',
  planId,
  studentId,
  payload,
}) =>
  client
    .request({
      url: `${version}/ilp/student-plan/${planId}/${studentId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Submits a student's plan
 * @param planId {string} - The uuid of the current ilp element
 * @param studentId {string} - Required if K12admin
 * @param payload.submittedDate
 */
export const submitStudentPlan = ({
  version = 'v1',
  planId,
  studentId,
  payload,
}) =>
  client
    .request({
      url: studentId
        ? `${version}/ilp/student-plan/${planId}/${studentId}/`
        : `${version}/ilp/student-plan/${planId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to fetch all plans for a student. Used by student users only
 */
export const retrievePlans = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ilp/plans/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to fetch all plans for a student. This is used by non student users
 * @param studentId {string} - Required for non student users
 */
export const retrieveStudentPlans = ({ version = 'v1', studentId }) =>
  client
    .request({
      url: `${version}/ilp/plans/${studentId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to fetch plan for meetings and notes. This is used by non student users
 * @param studentId {string} - Required for non student users
 */
export const retrieveStudentNotePlan = ({
  version = 'v1',
  studentId,
  params,
}) =>
  client
    .request({
      url: `${version}/ilp/plans/${studentId}/notes/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to unapprove/undo a student's plan
 * @param planId {string} - The ilp id
 * @param studentId {string} - The id of the student
 * @param payload.guardianApprovedDate - If true, unapprove as a guardian
 * @param payload.schoolAdminApprovedDate - else unapprove as schoolAdmin
 */
export const unapproveStudentPlan = ({
  version = 'v1',
  planId,
  studentId,
  payload,
}) =>
  client
    .request({
      url: `${version}/ilp/student-plan/${planId}/${Number(studentId)}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to update the guardian approval status with an approval method
 * @param planId {string} - The ilp id
 * @param studentId {string} - The id of the student
 * @param payload.guardianOfflineApprovalMethod {string} - There are 6 different options
 * @param payload.guardianApprovedDate - Current datetime
 */
export const updateGuardianApprovalStatus = ({
  version = 'v1',
  planId,
  studentId,
  payload,
}) =>
  client
    .request({
      url: `${version}/ilp/student-plan/${planId}/${studentId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by logged in users to retrieve course plan history
 * @param studentId {string} - Required if k12admin
 */
export const retrievePlanHistory = ({ version = 'v1', studentId }) =>
  client
    .request({
      url: studentId
        ? `${version}/cp-student-plans/student-plan/${studentId}/history/`
        : `${version}/cp-student-plans/student-plan/history/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to create a new course plan
 * @param studentId {string} - Required if k12admin
 */
export const createNewPlan = ({ version = 'v1', studentId }) =>
  client
    .request({
      url: studentId
        ? `${version}/cp-student-plans/student-plan/${studentId}/`
        : `${version}/cp-student-plans/student-plan/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by logged in users to download pgp report
 * @param payload.districtIlp {string} - The district ilp id
 * @param payload.students {array} - a list of student ids
 * @param payload.tzoffset
 */
export const generatePGPReport = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/ilp/pgp-report/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by non auth guardian to authenticate as OAuth
 * @param clientId {string} - Given by url params
 * @param clientSecret {string} - Given by url params
 */
const oAuth2Client = createClient({ oauth2: true });
export const authenticateGuardianILPUser = ({
  version = 'v1',
  clientId,
  clientSecret,
}) => {
  removeJwt();
  clearSessionStorageJwtKey();
  return oAuth2Client
    .request({
      url: `${version}/oauth2/authenticate/`,
      method: RequestMethod.POST,
      data: {
        clientId,
        clientSecret,
        grantType: 'client_credentials',
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

/**
 * Used by non auth guardian to fetch ilp plan details
 * @param studentIlpId - The student's ilp id
 */
export const guardianRetrievePlanDetail = ({ version = 'v1', studentIlpId }) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/${studentIlpId}/`,
      method: RequestMethod.GET,
      headers: {
        Authorization: `bearer ${getCookie(JWT)}`,
      },
      withCredentials: false,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to update the unread comments count for ILP comments drawer
 * @param studentIlpId - The student's ilp id
 * @param studentId - The id of the student
 */
export const markCommentsRead = ({ version = 'v1', studentIlpId, studentId }) =>
  client
    .request({
      url: `${version}/ilp/student-ilps/${studentIlpId}/read-comments/`,
      method: RequestMethod.GET,
      params: {
        student: studentId,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by non auth guardians to approve a student's plan
 * @param studentIlpId {string} - The student's ilp id
 * @param payload.nonLoggedInGuardianApprovalName {string} - Name of guardian
 */
export const guardianApproveStudentPlan = ({
  version = 'v1',
  studentIlpId,
  payload,
}) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/${studentIlpId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to download course plan reports
 * @param payload.studentId {string} - the id of the student
 * @param payload.schoolYear {string} - school year requested
 * @param payload.tzoffset
 * @param payload.reportType
 * @param payload.combinedFile
 * @param payload.evenPageCount
 */
export const downloadCoursePlanReport = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plan/report/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by non auth guardians to download course plan info
 * @param studentIlpId {string} - The student's ilp id
 */
export const guardianDownloadCoursePlanReport = ({
  version = 'v1',
  studentIlpId,
}) => {
  const d = new Date();
  const tzoffset = d.getTimezoneOffset();
  return client
    .request({
      url: `${version}/ilp/guardian-ilp/${studentIlpId}/course-plan-download/?tzoffset=${tzoffset}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

/**
 * Used by non auth guardians to download pgp report
 * @param studentIlpId {string} - The student's ilp id
 */
export const guardianDownloadPGPReport = ({ version = 'v1', studentIlpId }) => {
  const d = new Date();
  const tzoffset = d.getTimezoneOffset();
  return client
    .request({
      url: `${version}/ilp/guardian-ilp/${studentIlpId}/ilp-download/?tzoffset=${tzoffset}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

/**
 * Used by non auth guardians to fetch course plan history
 * @param studentIlpId {string} - The student's ilp id
 */
export const guardianRetrievePlanHistory = ({ version = 'v1', studentIlpId }) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/${studentIlpId}/course-plan-history/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by non auth guardians to fetch ilp element details
 * @param studentIlpId {string} - The student's ilp id
 * @param ilpElementUuid {string} - The plan element's uuid
 */
export const getElementDetailsForGuardian = ({
  version = 'v1',
  studentIlpId,
  ilpElementUuid,
}) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/${studentIlpId}/ilp-element-details/${ilpElementUuid}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by manage reminders modal to update guardian email
 * @param districtIlpUuid {string} - The current district's ilp uuid
 */
export const getDistrictIlp = ({ version = 'v1', districtIlpUuid }) =>
  client
    .request({
      url: `${version}/ilp/district-ilps/${districtIlpUuid}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by manage reminders modal to update guardian email
 * @param districtIlpUuid {string} - The current district's ilp uuid
 * @param payload.body {string} - body of email
 * @param payload.signature {string} - signature of email
 * @param payload.guardianEmailDates {string[]} - array of dates
 */
export const updateGuardianEmail = ({
  version = 'v1',
  districtIlpUuid,
  payload,
}) =>
  client
    .request({
      url: `${version}/ilp/district-ilps/${districtIlpUuid}/guardian-email-schedule/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve final list config for student ilp
 * @param {string} version
 * @param {number} studentIlp
 */
export const getFinalListConfiguration = ({
  version = 'v1',
  studentIlp,
} = {}) =>
  client
    .request({
      url: `${version}/ilp/guardian-ilp/${studentIlp}/final-list-configuration/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);
